<template>
  <div
    class="menu-item-wrapper"
    @mouseover="handleMenuItemMouseOver"
    @mouseleave.stop="handleMenuItemMouseLeave"
  >
    <div v-if="item.topDivider" class="menu-item-divider"></div>
    <sl-tooltip
      v-if="!item.children || !item.children.length"
      :content="item.tooltip"
      :disabled="!item.tooltip"
      :placement="tooltipPlacement"
      :style="tooltipMaxWidthPx"
      hoist
    >
      <div
        :class="[
          'menu-item',
          { disabled: item.disabled || item.loading },
          { loading: item.loading },
          item.cssClass,
        ]"
        :aria-busy="item.loading ? 'true' : undefined"
        @click.stop="handleMenuItemClick({ value: item.value })"
      >
        <span v-if="item.loading" class="icon-spinner spin menu-item-icon"></span>
        <span v-else-if="item.icon" :class="[item.icon, 'menu-item-icon']"></span>
        <span class="menu-item-text">{{ item.label }}</span>
      </div>
    </sl-tooltip>
    <mc-menu
      v-else
      ref="childMenu"
      :items="item.children"
      :search-placeholder="item.searchPlaceholder"
      :has-search="item.hasSearch"
      :menu-distance="0"
      @click:item="handleMenuItemClick"
    >
      <template #buttonAction>
        <sl-tooltip
          :content="item.tooltip"
          :disabled="!item.tooltip"
          hoist
          :placement="tooltipPlacement"
          :style="tooltipMaxWidthPx"
        >
          <div
            :class="[
              'menu-item',
              { disabled: item.disabled || item.loading },
              { loading: item.loading },
              { active: isChildMenuOpen },
              item.cssClass,
            ]"
            :aria-busy="item.loading ? 'true' : undefined"
          >
            <span v-if="item.loading" class="icon-spinner spin menu-item-icon"></span>
            <span v-else-if="item.icon" :class="[item.icon, 'menu-item-icon']"></span>
            <span class="menu-item-text">{{ item.label }}</span>
            <span class="icon-chevron-right menu-item-suffix-icon"></span>
          </div>
        </sl-tooltip>
      </template>
    </mc-menu>
    <div v-if="item.bottomDivider" class="menu-item-divider"></div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import _ from "lodash";
import "@shoelace-style/shoelace/dist/components/tooltip/tooltip.js";
import McMenu from "@/components/ui/McMenu/McMenu.vue";
import { MENU_CLOSE_DELAY } from "@/constants/menu";
import { IMenuItem, IMenuItemEvent } from "@/models/menu.model";

interface IMcMenuItemProps {
  item: IMenuItem;
  tooltipPlacement?: string;
  tooltipMaxWidth?: number;
}

const props = withDefaults(defineProps<IMcMenuItemProps>(), {
  tooltipPlacement: "right",
  tooltipMaxWidth: 260,
});

const { item, tooltipPlacement, tooltipMaxWidth } = toRefs(props);

const childMenu = ref();
const isChildMenuOpen = computed(() => childMenu.value?.isMenuOpen);
const isPointerInsideMenuItem = ref(false);

const tooltipMaxWidthPx = computed(() => `--max-width: ${tooltipMaxWidth.value}px;`);

const emits = defineEmits(["click"]);

const handleMenuItemClick = (menuEvent: IMenuItemEvent) => {
  if (item.value.disabled || item.value.loading) return;

  if (item.value.children && item.value.children.length) {
    menuEvent = {
      value: _.cloneDeep(item.value.value),
      childValue: { value: _.cloneDeep(menuEvent.value) },
    };
  }

  emits("click", menuEvent);
};

const handleMenuItemMouseOver = () => {
  isPointerInsideMenuItem.value = true;
  if (childMenu.value && !isChildMenuOpen.value) {
    childMenu.value.openMenu();
  }
};

const handleMenuItemMouseLeave = () => {
  isPointerInsideMenuItem.value = false;
  setTimeout(() => {
    if (!isPointerInsideMenuItem.value && isChildMenuOpen.value) {
      childMenu.value.closeMenu();
    }
  }, MENU_CLOSE_DELAY);
};
</script>

<style>
:root {
  --mc-menu-item-height: 32px;
  --mc-menu-item-padding: 0px 8px 0 8px;
  --mc-menu-item-border-radius: 4px;
  --mc-menu-item-background-color: transparent;
  --mc-menu-item-background-color-hover: var(--colour-background-brand-default);
  --mc-menu-item-icon-color: var(--colour-icons-brand-default);
  --mc-menu-item-icon-size: 16px;
  --mc-menu-item-icon-wrapper-size: 24px;
  --mc-menu-item-suffix-icon-color: var(--colour-icons-neutral-subtle);
  --mc-menu-item-suffix-icon-size: 20px;
  --mc-menu-item-suffix-icon-width: 16px;
  --mc-menu-item-suffix-icon-height: 24px;
  --mc-menu-item-suffix-font-weight: var(--font-weight-body-bold);
  --mc-menu-item-text-color: var(--colour-text-neutral-default);
  --mc-menu-item-font-size: var(--font-size-body-regular);
  --mc-menu-item-divider-margin: 6px 8px;
  --mc-menu-item-divider-height: 1px;
  --mc-menu-item-divider-color: var(--colour-border-neutral-subtle);

  --mc-menu-item-icon-color-disabled: var(--colour-icons-disabled);
  --mc-menu-item-text-color-disabled: var(--colour-text-disabled);
  --mc-menu-item-background-color-disabled: transparent;
  --mc-menu-item-background-color-hover-disabled: transparent;
}
</style>

<style lang="scss" scoped>
.menu-item-wrapper {
  --mc-menu-scroll-area-max-height: 135px;

  width: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;

  .menu-item {
    display: flex;
    align-items: center;
    width: 100%;
    height: var(--mc-menu-item-height);
    padding: var(--mc-menu-item-padding);
    background-color: var(--mc-menu-item-background-color);

    .menu-item-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--mc-menu-item-icon-wrapper-size);
      height: var(--mc-menu-item-icon-wrapper-size);
      margin-right: 6px;
      color: var(--mc-menu-item-icon-color);
      font-size: var(--mc-menu-item-icon-size);
    }

    .menu-item-text {
      color: var(--mc-menu-item-text-color);
      font-size: var(--mc-menu-item-font-size);
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      user-select: none;
    }

    .menu-item-suffix-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--mc-menu-item-suffix-icon-width);
      height: var(--mc-menu-item-suffix-icon-height);
      color: var(--mc-menu-item-icon-color);
      font-size: var(--mc-menu-item-suffix-icon-size);
      font-weight: var(--mc-menu-item-suffix-font-weight);
    }

    &.active,
    &:hover {
      border-radius: var(--mc-menu-item-border-radius);
      background-color: var(--mc-menu-item-background-color-hover);
    }

    &.disabled {
      cursor: not-allowed;
      background-color: var(--mc-menu-item-background-color-disabled);

      .menu-item-icon {
        color: var(--mc-menu-item-icon-color-disabled);
      }

      .menu-item-text {
        color: var(--mc-menu-item-text-color-disabled);
      }

      &:hover {
        background-color: var(--mc-menu-item-background-color-hover-disabled);
      }
    }
  }

  .menu-item-divider {
    display: flex;
    height: var(--mc-menu-item-divider-height);
    margin: var(--mc-menu-item-divider-margin);
    background-color: var(--mc-menu-item-divider-color);
  }
}
</style>
